import { useEffect, useState } from "react";
import { Save, MapPin, Music2, Upload, Image as ImageIcon, Calendar, Copy, RefreshCw, ExternalLink, Crown, Sparkles, Star, Gift, Pencil, Check, X, KeyRound } from "lucide-react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { INSTRUMENTS, VOICE_TYPES, FEE_PREFS } from "../lib/instruments";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link, useLocation } from "react-router-dom";
import AnnouncementEmailPref from "../components/AnnouncementEmailPref";
import ScopePromptCard from "../components/ScopePromptCard";

function ScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [hash]);
  return null;
}

function InviteCard() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;

  const copy = (text, label = "Link") => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Couldn't copy"),
    );
  };

  const saveVanity = async () => {
    setBusy(true);
    try {
      const { data: out } = await api.post("/referrals/customize", { code: draft });
      toast.success(`Your code is now ${out.code}`);
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const progressToFounding = Math.min(3, data.referral_count);

  return (
    <section className="card-base p-5" data-testid="invite-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-terracotta-50 text-terracotta-600 grid place-items-center shrink-0">
          <Gift size={18} />
        </div>
        <div className="flex-1">
          <div className="label-overline">Invite friends</div>
          <h2 className="font-heading font-semibold">Bring your section to AMDS</h2>
          <p className="text-sm text-ink-600 mt-0.5">
            Every signup earns you 1 month of free Platinum credit (banked for when paid billing launches).
            Get to 3 and you become an AMDS Founding Member.
          </p>
        </div>
      </div>

      {/* Code + share link */}
      <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-stretch mb-3">
        {editing ? (
          <div className="flex items-center gap-2 input-field !p-2">
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20))}
              className="flex-1 bg-transparent outline-none font-mono tracking-wider"
              placeholder="3-20 letters or numbers"
              data-testid="invite-vanity-input"
            />
            <button onClick={saveVanity} disabled={busy || draft.length < 3} className="btn-primary !py-1.5 !px-3" data-testid="invite-vanity-save">
              <Check size={14} /> Save
            </button>
            <button onClick={() => setEditing(false)} className="btn-secondary !py-1.5 !px-3" data-testid="invite-vanity-cancel">
              <X size={14} />
            </button>
          </div>
        ) : (
          <div className="input-field flex items-center justify-between gap-2 !py-3">
            <span className="font-mono tracking-wider text-lg" data-testid="invite-code">{data.code}</span>
            {!data.customized && (
              <button
                onClick={() => { setDraft(data.code); setEditing(true); }}
                className="text-sm text-sage-600 hover:text-sage-700 inline-flex items-center gap-1"
                data-testid="invite-customize-btn"
              >
                <Pencil size={14} /> Customise once
              </button>
            )}
          </div>
        )}
        <button onClick={() => copy(data.share_link, "Share link")} className="btn-secondary" data-testid="invite-copy-link">
          <Copy size={14} /> Copy link
        </button>
      </div>
      <div className="text-xs text-ink-500 break-all bg-ivory-100 rounded-xl p-2 mb-4" data-testid="invite-share-link">
        {data.share_link}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-xl bg-sage-50 border border-sage-100 p-3" data-testid="invite-count">
          <div className="font-heading text-2xl font-bold">{data.referral_count}</div>
          <div className="text-xs text-ink-600">invites</div>
        </div>
        <div className="rounded-xl bg-amber-soft/40 border border-amber-soft p-3" data-testid="invite-credits">
          <div className="font-heading text-2xl font-bold">{data.pending_credit_months}</div>
          <div className="text-xs text-ink-600">months credited</div>
        </div>
        <div className={`rounded-xl border p-3 ${data.founding_member ? "bg-terracotta-500 text-white border-terracotta-500" : "bg-ivory-100 border-ivory-200"}`} data-testid="invite-founding">
          <div className="font-heading text-2xl font-bold">{progressToFounding}/3</div>
          <div className="text-xs opacity-80">{data.founding_member ? "Founding Member ✦" : "to Founding"}</div>
        </div>
      </div>

      <p className="text-xs text-ink-500 mt-3">
        Share the code or the link — when someone signs up with it, we'll add a thank-you credit to your account.
      </p>
    </section>
  );
}

function PricingSummary() {
  const [sub, setSub] = useState(null);
  useEffect(() => { api.get("/billing/me").then(({ data }) => setSub(data)).catch(() => {}); }, []);
  if (!sub) return null;
  const isGrandfathered = sub.tier === "grandfathered";
  const isTrial = sub.tier === "platinum_trial";
  const isPaid = sub.is_platinum && !isTrial && !isGrandfathered;
  return (
    <section className="card-base p-5" data-testid="pricing-summary-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl grid place-items-center ${sub.is_platinum ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`}>
            {isGrandfathered ? <Star size={18} /> : sub.is_platinum ? <Crown size={18} /> : <Sparkles size={18} />}
          </div>
          <div>
            <div className="label-overline">Your plan</div>
            <h2 className="font-heading font-semibold capitalize">
              {isGrandfathered ? "Early-adopter Platinum" : isTrial ? "Platinum (trial)" : sub.is_platinum ? "Platinum" : "Free"}
            </h2>
            <p className="text-sm text-ink-600 mt-0.5">
              {isGrandfathered && "Thanks for being here from the start — Platinum is on us, forever."}
              {isTrial && sub.subscription_ends_at && `Trial ends ${new Date(sub.subscription_ends_at).toLocaleDateString()}`}
              {isPaid && sub.subscription_ends_at && `Renews ${new Date(sub.subscription_ends_at).toLocaleDateString()}`}
              {!sub.is_platinum && "Upgrade to unlock unlimited ensembles, gig posting, attendance, dep tools and more."}
            </p>
          </div>
        </div>
        <Link to="/pricing" className={sub.is_platinum ? "btn-secondary text-sm" : "btn-primary text-sm"} data-testid="pricing-link">
          {sub.is_platinum ? "Manage" : "Upgrade"}
        </Link>
      </div>
    </section>
  );
}

export default function Profile() {
  const { user, refresh: refreshUser } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [avatarBlob, setAvatarBlob] = useState(null);
  const [sub, setSub] = useState(null);

  const loadProfile = async () => {
    const { data } = await api.get("/profile/me");
    setForm({
      name: data.name || user?.name || "",
      instrument: data.instrument || "",
      secondary_instruments: data.secondary_instruments || [],
      voice_type: data.voice_type || "",
      location: data.location || "",
      travel_radius_km: data.travel_radius_km || 25,
      ensemble_experience: data.ensemble_experience || "",
      bio: data.bio || "",
      fee_preference: data.fee_preference || "any",
      own_transport: !!data.own_transport,
      contact_pref: data.contact_pref || "in_app",
      image_url: data.image_url || "",
      phone: data.phone || "",
      ensembles_led: data.ensembles_led || "",
      repertoire_highlights: data.repertoire_highlights || "",
      playing_level: data.playing_level || "",
      years_playing: data.years_playing ?? "",
      training: data.training || "",
      notable_performances: data.notable_performances || "",
      links: data.links || "",
      avatar_path: data.avatar_path || "",
    });
    if (data.avatar_path) {
      try {
        const res = await fetch(`${API}/files/${data.avatar_path}`, { credentials: "include" });
        if (res.ok) {
          const b = await res.blob();
          setAvatarBlob(URL.createObjectURL(b));
        }
      } catch {}
    }
  };

  useEffect(() => {
    loadProfile();
    api.get("/calendar/subscription").then(({ data }) => setSub(data)).catch(() => {});
    return () => { if (avatarBlob) URL.revokeObjectURL(avatarBlob); };
  // eslint-disable-next-line
  }, []);

  if (!form) return <div className="text-ink-500">Loading…</div>;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      delete payload.avatar_path;
      await api.put("/profile/me", payload);
      toast.success("Profile saved");
      refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (5MB max)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/uploads/avatar`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Upload failed"); }
      toast.success("Photo updated");
      loadProfile();
    } catch (err) { toast.error(err.message); }
    finally { e.target.value = ""; }
  };

  const copy = (s) => { navigator.clipboard.writeText(s); toast.success("Copied"); };
  const rotate = async () => {
    if (!window.confirm("Rotate the calendar URL? Old subscriptions will stop updating.")) return;
    const { data } = await api.post("/calendar/rotate-token");
    setSub(data);
    toast.success("Calendar URL rotated");
  };

  const isConductor = user?.role === "conductor";
  const isMusician = user?.role === "musician";

  return (
    <div className="space-y-5 max-w-2xl" data-testid="profile-page">
      <ScrollToHash />
      <div>
        <div className="label-overline">Profile</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Your AMDS card</h1>
      </div>

      {/* Pricing & subscription */}
      <PricingSummary />
      <ScopePromptCard />
      <InviteCard />
      <AnnouncementEmailPref />

      {/* Avatar */}
      <section className="card-base p-5 flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl bg-sage-100 text-sage-600 grid place-items-center overflow-hidden">
          {avatarBlob ? (
            <img src={avatarBlob} alt="Avatar" className="w-full h-full object-cover" data-testid="avatar-img" />
          ) : (
            <ImageIcon size={28} />
          )}
        </div>
        <div className="flex-1">
          <div className="font-heading font-semibold">{form.name}</div>
          <div className="text-sm text-ink-500">{user?.email}</div>
          <label className="btn-secondary text-sm mt-2 inline-flex cursor-pointer" data-testid="avatar-upload-label">
            <Upload size={14} /> Change photo
            <input type="file" hidden accept="image/jpeg,image/png,image/webp,image/gif" onChange={onAvatar} data-testid="avatar-upload-input" />
          </label>
        </div>
      </section>

      {/* Calendar subscription */}
      {sub && (
        <section className="card-base p-5 space-y-3" data-testid="calendar-subscription">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center"><Calendar size={18} /></div>
              <div>
                <h2 className="font-heading font-semibold">Live calendar feed</h2>
                <p className="text-sm text-ink-600">Subscribe once and your AMDS bookings stay in sync — Apple, Google, Outlook, anywhere.</p>
              </div>
            </div>
            <button onClick={rotate} className="btn-ghost text-xs" data-testid="rotate-token-btn"><RefreshCw size={14} /> Rotate</button>
          </div>
          <div className="space-y-2">
            <a href={sub.webcal_url} className="btn-primary text-sm w-fit" data-testid="webcal-subscribe-btn"><ExternalLink size={14} /> Subscribe in calendar</a>
            <div className="flex items-center gap-2 text-xs text-ink-500 bg-ivory-100 rounded-xl p-3 break-all" data-testid="webcal-url-text">
              <code className="flex-1 font-mono">{sub.webcal_url}</code>
              <button onClick={() => copy(sub.webcal_url)} className="btn-ghost shrink-0" data-testid="copy-webcal-btn"><Copy size={14} /></button>
            </div>
          </div>
        </section>
      )}

      <form onSubmit={submit} className="card-base p-5 sm:p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label-overline block mb-2">Display name</label>
            <input className="input-field" value={form.name} onChange={(e) => setField("name", e.target.value)} required data-testid="profile-name-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Location</label>
            <div className="relative">
              <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
              <input className="input-field pl-9" value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="Oxford, UK" data-testid="profile-location-input" />
            </div>
          </div>
        </div>

        {isMusician && (
          <>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline block mb-2">Main instrument</label>
                <div className="relative">
                  <Music2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                  <select className="input-field pl-9" value={form.instrument} onChange={(e) => setField("instrument", e.target.value)} data-testid="profile-instrument-select">
                    <option value="">Select instrument</option>
                    {INSTRUMENTS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="label-overline block mb-2">Voice type</label>
                <select className="input-field" value={form.voice_type} onChange={(e) => setField("voice_type", e.target.value)} data-testid="profile-voice-select">
                  {VOICE_TYPES.map((v) => <option key={v} value={v}>{v || "—"}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="label-overline block mb-2">Secondary instruments</label>
              <div className="flex flex-wrap gap-2" data-testid="profile-secondary-list">
                {INSTRUMENTS.filter((i) => i !== form.instrument).map((i) => {
                  const on = form.secondary_instruments.includes(i);
                  return (
                    <button key={i} type="button" onClick={() => {
                      const has = form.secondary_instruments.includes(i);
                      setField("secondary_instruments", has ? form.secondary_instruments.filter((x) => x !== i) : [...form.secondary_instruments, i]);
                    }} className={`pill border ${on ? "bg-sage-500 text-white border-sage-500" : "bg-white text-ink-700 border-ivory-200"}`} data-testid={`profile-sec-${i}`}>
                      {i}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline block mb-2">Travel radius (km)</label>
                <input type="number" min={0} max={2000} className="input-field" value={form.travel_radius_km} onChange={(e) => setField("travel_radius_km", Number(e.target.value))} data-testid="profile-radius-input" />
              </div>
              <div>
                <label className="label-overline block mb-2">Fee preference</label>
                <select className="input-field" value={form.fee_preference} onChange={(e) => setField("fee_preference", e.target.value)} data-testid="profile-fee-select">
                  {FEE_PREFS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="label-overline block mb-2">Ensemble experience</label>
              <textarea rows={2} className="input-field" value={form.ensemble_experience} onChange={(e) => setField("ensemble_experience", e.target.value)} data-testid="profile-experience-input" />
            </div>

            <div>
              <label className="label-overline block mb-2">Short bio</label>
              <textarea rows={3} className="input-field" value={form.bio} onChange={(e) => setField("bio", e.target.value)} data-testid="profile-bio-input" />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="label-overline block mb-2">Playing standard</label>
                <select className="input-field" value={form.playing_level} onChange={(e) => setField("playing_level", e.target.value)} data-testid="profile-level-select">
                  <option value="">Prefer not to say</option>
                  <option value="beginner">Beginner / improver</option>
                  <option value="intermediate">Intermediate (Grade 5+)</option>
                  <option value="advanced">Advanced (Grade 8 standard)</option>
                  <option value="grade_8_plus">Grade 8 distinction or beyond</option>
                  <option value="diploma_conservatoire">Diploma / conservatoire-trained</option>
                  <option value="professional_standard">Professional standard (community ensembles)</option>
                </select>
              </div>
              <div>
                <label className="label-overline block mb-2">Years playing</label>
                <input type="number" min={0} max={99} className="input-field" value={form.years_playing ?? ""} onChange={(e) => setField("years_playing", e.target.value === "" ? "" : Number(e.target.value))} placeholder="e.g. 12" data-testid="profile-years-input" />
              </div>
            </div>

            <div>
              <label className="label-overline block mb-2">Training (optional)</label>
              <input className="input-field" value={form.training} onChange={(e) => setField("training", e.target.value)} placeholder="Royal Academy of Music 2018–22, private with X…" data-testid="profile-training-input" />
            </div>

            <div>
              <label className="label-overline block mb-2">Notable performances (optional)</label>
              <textarea rows={2} className="input-field" value={form.notable_performances} onChange={(e) => setField("notable_performances", e.target.value)} placeholder="Mahler 5 (Sheldonian, 2024); Brahms VC (Tower of London, 2023)…" data-testid="profile-notable-input" />
            </div>

            <div>
              <label className="label-overline block mb-2">Recordings / website (optional)</label>
              <input className="input-field" value={form.links} onChange={(e) => setField("links", e.target.value)} placeholder="https://soundcloud.com/…  https://my-music.example" data-testid="profile-links-input" />
            </div>

            <label className="flex items-center gap-2 text-sm select-none cursor-pointer" data-testid="profile-transport-label">
              <input type="checkbox" checked={form.own_transport} onChange={(e) => setField("own_transport", e.target.checked)} className="w-5 h-5 accent-sage-500" data-testid="profile-transport-checkbox" />
              I have my own transport
            </label>
          </>
        )}

        {isConductor && (
          <>
            <div>
              <label className="label-overline block mb-2">Bio</label>
              <textarea rows={3} className="input-field" value={form.bio} onChange={(e) => setField("bio", e.target.value)} placeholder="A few lines about your conducting." data-testid="profile-bio-input" />
            </div>
            <div>
              <label className="label-overline block mb-2">Ensembles led</label>
              <textarea rows={2} className="input-field" value={form.ensembles_led} onChange={(e) => setField("ensembles_led", e.target.value)} placeholder="Oxford Sinfonia, Magdalen Choir…" data-testid="profile-ensembles-led-input" />
            </div>
            <div>
              <label className="label-overline block mb-2">Repertoire highlights</label>
              <textarea rows={2} className="input-field" value={form.repertoire_highlights} onChange={(e) => setField("repertoire_highlights", e.target.value)} placeholder="Mahler 5, Verdi Requiem, Britten WR…" data-testid="profile-rep-highlights-input" />
            </div>
          </>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label-overline block mb-2">Contact preference</label>
            <select className="input-field" value={form.contact_pref} onChange={(e) => setField("contact_pref", e.target.value)} data-testid="profile-contact-pref-select">
              <option value="in_app">In-app messages only</option>
              <option value="email">Show email to bookers</option>
              <option value="phone">Show phone to bookers</option>
            </select>
          </div>
          <div>
            <label className="label-overline block mb-2">Phone (optional)</label>
            <input className="input-field" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+44 7700 900123" data-testid="profile-phone-input" />
          </div>
        </div>

        <div className="pt-2 flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="profile-save-btn">
            <Save size={16} /> {busy ? "Saving…" : "Save profile"}
          </button>
        </div>
      </form>

      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const { refresh } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (form.new_password.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (form.new_password !== form.confirm) { toast.error("New passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <section id="password" className="card-base p-4 sm:p-5 mt-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-3">
        <KeyRound size={16} className="text-sage-600" />
        <h2 className="font-heading text-lg font-semibold">Password</h2>
      </div>
      <p className="text-sm text-ink-600 mb-4">Change your AMDS password. You'll stay signed in on this device.</p>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="label-overline block mb-2">Current password</label>
          <input type="password" required autoComplete="current-password" className="input-field"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            data-testid="current-password-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label-overline block mb-2">New password</label>
            <input type="password" required minLength={6} autoComplete="new-password" className="input-field"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              data-testid="new-password-input" />
          </div>
          <div>
            <label className="label-overline block mb-2">Confirm new password</label>
            <input type="password" required minLength={6} autoComplete="new-password" className="input-field"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              data-testid="confirm-password-input" />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={busy} className="btn-primary" data-testid="change-password-btn">
            <KeyRound size={14} /> {busy ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </section>
  );
}
